import {
  db,
  eventsCollectionRef,
  eventRef,
  storage,
  siteRef,
} from '../services/firebase'
import {
  collectionGroup,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  getDoc,
  writeBatch,
  doc,
  Timestamp,
  updateDoc,
  Query,
  deleteDoc,
} from 'firebase/firestore'
import { ref, getDownloadURL } from 'firebase/storage'
import { EventViewModel } from '../pages/ViewModel'
import { isStringValid } from '../utils/isValid'
import { removeEmpty } from '../utils/object'
import { stringToDate } from './shared'
import { getAllSites } from './Sites'

// TODO: Add function description all

export async function getEvent(
  siteId: string,
  eventId: string
): Promise<EventViewModel> {
  const docSnap = await getDoc(eventRef(siteId, eventId))

  if (docSnap.exists()) {
    let data = docSnap.data() as EventViewModel
    data = { ...data, id: eventId }

    if (data.upstreamImageRefs && data.upstreamImageRefs.length !== 0) {
      const upstreamRefPromises = data.upstreamImageRefs.map(
        async (imageRef) => {
          const url = await getDownloadURL(
            ref(storage, `sites/${siteId}/${eventId}/${imageRef}`)
          )
          return url
        }
      )
      data.upstreamImageRefs = await Promise.all(upstreamRefPromises)
    }

    if (data.downstreamImageRefs && data.downstreamImageRefs.length !== 0) {
      const downstreamRefPromises = data.downstreamImageRefs.map(
        async (imageRef) => {
          const url = await getDownloadURL(
            ref(storage, `sites/${siteId}/${eventId}/${imageRef}`)
          )
          return url
        }
      )
      data.downstreamImageRefs = await Promise.all(downstreamRefPromises)
    }

    return data
  }

  return null
}

/**
 * Query monitoring events based on Site Id/Site Code
 *
 * @param {String} siteId site id/ site code
 * @return array of events
 */
export async function getSiteEvents(
  siteId: string,
  paginateLimit?: number
): Promise<EventViewModel[]> {
  const events: EventViewModel[] = []

  let equery = query(
    eventsCollectionRef(siteId),
    orderBy('recordTimestamp', 'desc')
  )

  if (paginateLimit && paginateLimit > 0) {
    equery = query(
      eventsCollectionRef(siteId),
      orderBy('recordTimestamp', 'desc'),
      limit(paginateLimit)
    )
  }

  const eventDocsSnap = await getDocs(equery)

  eventDocsSnap.forEach((eventDoc) => {
    const eventData = { ...eventDoc.data(), id: eventDoc.id } as EventViewModel
    events.push(eventData)
  })

  return events
}

/**
 * Query recent monitoring events from years ago from one site
 */
export async function getRecentEventsFrom(
  siteId: string,
  yearsAgo?: number,
  limitNumber?: number
): Promise<EventViewModel[]> {
  const events: EventViewModel[] = []

  if (yearsAgo == null) yearsAgo = 5
  if (limitNumber == null) limitNumber = 5

  const definedYearsAgo = new Date(
    new Date().setFullYear(new Date().getFullYear() - yearsAgo)
  )

  const equery = query(
    eventsCollectionRef(siteId),
    where('recordTimestamp', '>', definedYearsAgo),
    orderBy('recordTimestamp', 'desc'),
    limit(limitNumber)
  )

  const eventDocsSnap = await getDocs(equery)

  eventDocsSnap.forEach((eventDoc) => {
    const eventData = { ...eventDoc.data(), id: eventDoc.id } as EventViewModel
    events.push(eventData)
  })

  return events
}

export async function getRecentEvents(
  limitNumber?: number
): Promise<EventViewModel[]> {
  const events: EventViewModel[] = []

  if (!limitNumber) limitNumber = 10

  const eventsQuery: Query = query(
    collectionGroup(db, 'events'),
    orderBy('recordTimestamp', 'desc'),
    limit(limitNumber)
  )

  const querySnapshot = await getDocs(eventsQuery)
  querySnapshot.forEach((eventDoc) => {
    const eventData = { ...eventDoc.data(), id: eventDoc.id } as EventViewModel
    events.push(eventData)
  })

  return events
}

export async function getRecentEventsFromRegion(
  region: string[],
  limitNumber?: number
): Promise<EventViewModel[]> {
  const events: EventViewModel[] = []

  const sites = await getAllSites()
  const sitesOfRegion = sites.filter((site) => region.includes(site.region))
  const siteCodes = sitesOfRegion.map((s) => s.id)

  if (!limitNumber) limitNumber = 10

  const eventsQuery: Query = query(
    collectionGroup(db, 'events'),
    orderBy('recordTimestamp', 'desc'),
    limit(50)
  )
  const querySnapshot = await getDocs(eventsQuery)
  querySnapshot.forEach((eventDoc) => {
    const eventData = { ...eventDoc.data(), id: eventDoc.id } as EventViewModel
    if (siteCodes.includes(eventData.siteCode)) {
      events.push(eventData)
    }
  })

  return events.slice(0, 9)
}

// ----------- CREATE --------------

export async function addNewEvent(event: EventViewModel) {
  event = {
    ...event,
    timeOfRecord: isStringValid(event.timeOfRecord)
      ? event.timeOfRecord
      : '12:00',
  }
  removeEmpty(event)
  validateEvent(event)

  // [DO NOT DELETE] Adding 'recordTimestamp' in firebase
  const recordTimestampTemp = stringToDate(
    `${event.creationDate} ${event.timeOfRecord}`
  )
  const sendingEvent = {
    ...event,
    recordTimestamp: Timestamp.fromDate(recordTimestampTemp),
  }

  const batch = writeBatch(db)

  const eventId = doc(eventsCollectionRef(sendingEvent.siteCode)).id
  console.log(`Submitting Event: ${JSON.stringify(sendingEvent)}`)
  batch.set(eventRef(sendingEvent.siteCode, eventId), sendingEvent)

  await batch.commit()

  return eventId
}

export async function updateEvent(event: EventViewModel) {
  if (!isStringValid(event.id)) throw new Error('Empty event id')
  validateEvent(event)
  const eventId = event.id
  event = {
    ...event,
    timeOfRecord: isStringValid(event.timeOfRecord)
      ? event.timeOfRecord
      : '12:00',
  }
  removeEmpty(event)
  delete event['id']

  // [DO NOT DELETE] Updating 'recordTimestamp' in firebase
  const recordTimestampTemp = stringToDate(
    `${event.creationDate} ${event.timeOfRecord}`
  )
  const sendingEvent = {
    ...event,
    recordTimestamp: Timestamp.fromDate(recordTimestampTemp),
  }
  console.log(`Updating Event: ${JSON.stringify(sendingEvent)}`)
  await updateDoc(eventRef(sendingEvent.siteCode, eventId), sendingEvent)
}

function validateEvent(event: EventViewModel) {
  if (!isStringValid(event.siteCode)) throw new Error('Empty site code')
  if (!isStringValid(event.creationDate)) throw new Error('Empty date')
  if (!isStringValid(event.timeOfRecord)) throw new Error('Empty time')
}

// ---------- DELETE
/**
 *
 * Remove event from firebase
 *
 * @NOTE Currently removing an event will not remove following images
 *
 */
export async function removeEvent(siteId: string, eventId: string) {
  // TODO: Delete images

  await deleteDoc(eventRef(siteId, eventId))
}
